import React, { useState, useEffect } from "react";
import axios from "../utils/axios";
import { useParams, useNavigate } from "react-router-dom";
import Spinner from "../components/Spinner";
import { IoMdArrowDropleft } from "react-icons/io";
import { storage, legacyStorage } from "../firebaseConfig";
import { getDownloadURL, ref } from "firebase/storage";
import Config from "../utils/config";
import { IoMdOptions } from "react-icons/io";
import toast, { Toaster } from "react-hot-toast";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { AiOutlineDownload } from "react-icons/ai";

const ViewRecordDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);

  const [recordData, setRecordData] = useState(null);
  const [fileUrls, setFileUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [fileloading, setfileLoading] = useState(true);
  const [downloading, setDownloading] = useState({
    active: false,
    mode: null, // "single" | "all"
    currentLabel: "",
    done: 0,
    total: 0,
    errors: [],
  });
  const [selectedFileKey, setSelectedFileKey] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [status, setStatus] = useState("pending");
  const [comment, setComment] = useState("");
  const openModal = () => {
    if (recordData) {
      setStatus(recordData.status || "pending");
      setComment(recordData.comment ?? "");
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };
  useEffect(() => {
    const fetchRecordDetails = async () => {
      try {
        const response = await axios.get(`${Config.baseURL}/get-record/${id}`);
        const data = response.data;
        setRecordData(data);
        setStatus(data.status || "pending");
        setComment(data.comment ?? "");
      } catch (error) {
        toast.error(
          "Erreur lors de la récupération des détails de l'enregistrement"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRecordDetails();
  }, [id]);

  useEffect(() => {
    setfileLoading(true);
    const retrieveFiles = async () => {
      if (recordData) {
        const urls = {};
        const fileKeys = [
          "idCard",
          "idCardRecto",
          "carteVitale",
          "carteProfessionnelle",
          "diplomes",
          "justificatif",
          "rib",
          "photo",
          "cv",
          "attestationMedical",
          "healthCheckFile",
        ];

        const getFileUrl = async (filename) => {
          const path = `files/${filename}`;
          try {
            const primaryRef = ref(storage, path);
            return await getDownloadURL(primaryRef);
          } catch (primaryError) {
            try {
              const legacyRef = ref(legacyStorage, path);
              return await getDownloadURL(legacyRef);
            } catch (legacyError) {
              console.error(
                `Error getting download URL for ${filename} (new + legacy buckets):`,
                primaryError,
                legacyError,
              );
              return null;
            }
          }
        };

        for (const key of fileKeys) {
          if (recordData[key]) {
            const url = await getFileUrl(recordData[key]);
            if (url) {
              urls[key] = url;
            }
          }
        }
        setLoading(false);
        setFileUrls(urls);
        setfileLoading(false);
      }
    };

    retrieveFiles();
  }, [recordData]);

  const handleBack = () => {
    navigate(-1);
  };
  const handleViewInNewWindow = () => {
    if (selectedFileUrl) {
      window.open(selectedFileUrl, "_blank");
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "approved":
        return "Approuvé";
      case "pending":
        return "En cours";
      case "rejected":
        return "Rejeté";
      default:
        return "";
    }
  };

  const handleFileClick = (key, url) => {
    if (selectedFileKey === key) {
      setSelectedFileKey(null);
      setSelectedFileUrl(null);
    } else {
      setSelectedFileKey(key);
      setSelectedFileUrl(url);
    }
  };

  const getProxyUrl = (fileUrl) => {
    return `${Config.baseURL}/proxy-file?url=${encodeURIComponent(fileUrl)}`;
  };

  const parseProxyError = async (axiosError) => {
    try {
      const ct = axiosError?.response?.headers?.["content-type"] || "";
      const data = axiosError?.response?.data;

      // If we requested arraybuffer, errors can still come back as ArrayBuffer.
      if (ct.includes("application/json") && data) {
        const text =
          data instanceof ArrayBuffer
            ? new TextDecoder("utf-8").decode(new Uint8Array(data))
            : typeof data === "string"
              ? data
              : null;
        if (text) return JSON.parse(text);
        if (typeof data === "object") return data;
      }

      if (typeof data === "object" && data) return data;
    } catch { }
    return null;
  };

  const fetchViaProxy = async (fileUrl) => {
    // Use arraybuffer so we can parse JSON errors reliably.
    const response = await axios.get(getProxyUrl(fileUrl), {
      responseType: "arraybuffer",
      validateStatus: () => true,
    });

    const contentType = response.headers?.["content-type"] || "";
    if (response.status >= 400) {
      let details = null;
      if (contentType.includes("application/json")) {
        try {
          const text = new TextDecoder("utf-8").decode(new Uint8Array(response.data));
          details = JSON.parse(text);
        } catch { }
      }
      const msg =
        details?.upstreamBody ||
        details?.error ||
        `Téléchargement impossible (HTTP ${response.status})`;
      const err = new Error(msg);
      err.status = response.status;
      err.details = details;
      throw err;
    }

    return response;
  };

  const handleDownload = async (key, fileUrl) => {
    try {
      setDownloading({
        active: true,
        mode: "single",
        currentLabel: getFileLabel(key),
        done: 0,
        total: 1,
        errors: [],
      });

      const response = await fetchViaProxy(fileUrl);

      const contentType = response.headers["content-type"];
      let fileExtension = "bin";
      if (contentType) {
        const mimeToExt = {
          "application/pdf": "pdf",
          "image/jpeg": "jpg",
          "image/jpg": "jpg",
          "image/png": "png",
          "image/gif": "gif",
          "image/webp": "webp",
          "application/msword": "doc",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
          "text/plain": "txt",
        };
        fileExtension = mimeToExt[contentType] || contentType.split("/").pop() || "bin";
      }

      const downloadFilename = `${getFileLabel(key)}.${fileExtension}`;
      const fileBlob = new Blob([response.data], { type: contentType || "application/octet-stream" });
      const fileUrlObject = window.URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = fileUrlObject;
      link.setAttribute("download", downloadFilename);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(fileUrlObject);
      link.remove();
    } catch (error) {
      const msg =
        error?.message ||
        `Erreur lors du téléchargement de ${getFileLabel(key)}`;
      toast.error(msg);
      setDownloading((prev) => ({
        ...prev,
        errors: [...(prev?.errors || []), { key, label: getFileLabel(key), message: msg }],
      }));
    } finally {
      setDownloading((prev) => ({ ...prev, active: false, done: prev.total || 1 }));
    }
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };

  const handleCommentChange = (e) => {
    setComment(e.target.value);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.put(`${Config.baseURL}/update/${id}`, {
        status: status,
        comment: comment,
      });
      const { data } = await axios.get(`${Config.baseURL}/get-record/${id}`);
      setRecordData(data);
      setComment(data.comment ?? "");
      toast.success("Statut mis à jour");
      closeModal();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAll = async () => {
    const zip = new JSZip();
    const firstName = recordData.firstName;
    const lastName = recordData.lastName;

    try {
      const entries = Object.entries(fileUrls);
      setDownloading({
        active: true,
        mode: "all",
        currentLabel: "",
        done: 0,
        total: entries.length,
        errors: [],
      });

      let addedCount = 0;
      for (let i = 0; i < entries.length; i++) {
        const [key, url] = entries[i];
        try {
          setDownloading((prev) => ({
            ...prev,
            currentLabel: getFileLabel(key),
            done: i,
          }));

          const response = await fetchViaProxy(url);

          const contentType = response.headers["content-type"];
          const blob = new Blob([response.data], { type: contentType || "application/octet-stream" });
          if (!blob || blob.size === 0) continue;

          let fileExtension = "bin";
          if (contentType) {
            const mimeToExt = {
              "application/pdf": "pdf",
              "image/jpeg": "jpg",
              "image/jpg": "jpg",
              "image/png": "png",
              "image/gif": "gif",
              "image/webp": "webp",
              "application/msword": "doc",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
              "text/plain": "txt",
            };
            fileExtension = mimeToExt[contentType] || contentType.split("/").pop();
          } else {
            const urlParts = url.split(".");
            if (urlParts.length > 1) {
              fileExtension = urlParts.pop().split("?")[0];
            }
          }

          const filename = `${getFileLabel(key)}.${fileExtension}`;
          zip.file(filename, blob);
          addedCount++;
        } catch (error) {
          const msg =
            error?.message ||
            `Erreur lors du téléchargement de ${getFileLabel(key)}`;
          setDownloading((prev) => ({
            ...prev,
            errors: [...(prev?.errors || []), { key, label: getFileLabel(key), message: msg }],
          }));
        }
      }

      setDownloading((prev) => ({
        ...prev,
        currentLabel: "Création de l'archive…",
        done: prev.total,
      }));

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: {
          level: 6
        }
      });

      if (zipBlob.size === 0 || addedCount === 0) {
        toast.error("Aucun fichier valide à télécharger");
        return;
      }

      saveAs(zipBlob, `${firstName}-${lastName}-documents.zip`);
      const errorsCount = downloading?.errors?.length || 0;
      if (errorsCount > 0) {
        toast(`Archive créée avec ${errorsCount} erreur(s)`, { icon: "⚠️" });
      } else {
        toast.success("Archive créée");
      }
    } catch (error) {
      toast.error(error?.message || "Erreur lors de la création de l'archive");
    } finally {
      setDownloading((prev) => ({ ...prev, active: false }));
    }
  };

  if (loading || !recordData) {
    return <Spinner heading="Chargement..." />;
  }

  const emptyValue = (v) => (v == null || String(v).trim() === "" ? "—" : v);

  return (
    <>
      {downloading.active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden">
            {/* Header strip in brand yellow */}
            <div className="bg-yellow-400 px-5 py-3 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center">
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-white text-sm sm:text-base">
                  {downloading.mode === "all"
                    ? "Préparation du téléchargement des fichiers"
                    : "Téléchargement du fichier"}
                </div>
                <div className="text-xs text-white/90 truncate">
                  Merci de ne pas fermer cette fenêtre.
                </div>
              </div>
            </div>

            <div className="px-5 py-4">
              <div className="flex items-start gap-3">
                <div className="mt-1 h-9 w-9 rounded-full bg-yellow-50 flex items-center justify-center">
                  <svg
                    className="h-5 w-5 text-yellow-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 3v12m0 0 4-4m-4 4-4-4M5 19h14"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between text-sm text-gray-800">
                    <span className="font-medium">
                      {downloading.mode === "all"
                        ? "Création de l'archive ZIP"
                        : "Téléchargement en cours"}
                    </span>
                    <span className="text-xs text-gray-500">
                      {Math.min(downloading.done, downloading.total)}/
                      {downloading.total} fichiers
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-600 truncate">
                    {downloading.currentLabel || "Préparation des documents…"}
                  </div>

                  <div className="mt-3 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 transition-all"
                      style={{
                        width: `${downloading.total > 0
                          ? (Math.min(downloading.done, downloading.total) / downloading.total) * 100
                          : 10
                          }%`,
                      }}
                    />
                  </div>

                  {downloading.errors?.length > 0 && (
                    <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5">
                      Certains fichiers n'ont pas pu être téléchargés
                      automatiquement ({downloading.errors.length}). Les autres
                      fichiers continuent d'être ajoutés à l'archive.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 overflow-x-hidden w-full min-w-0">
        <button
          type="button"
          onClick={handleBack}
          className="mb-4 sm:mb-6 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-3 sm:py-2 rounded-lg text-base font-medium flex items-center transition-colors min-h-[44px] sm:min-h-0"
        >
          <IoMdArrowDropleft className="mr-2 flex-shrink-0" /> Retour
        </button>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <h2 className="text-xl sm:text-4xl font-semibold text-center mb-4 sm:mb-0">
              Status du dossier :{" "}
              <span
                className={`${recordData.status === "approved"
                  ? "text-green-500 bg-green-50 rounded-full py-1 px-3 sm:py-2 sm:px-4"
                  : recordData.status === "pending"
                    ? "text-orange-500 bg-orange-50 rounded-full py-1 px-3 sm:py-2 sm:px-4"
                    : "text-red-500 bg-red-50 rounded-full py-1 px-3 sm:py-2 sm:px-4"
                  }`}
              >
                {getStatusLabel(recordData.status)}
              </span>
            </h2>
            <button
              type="button"
              onClick={openModal}
              className="bg-blue-500 text-sm sm:text-base text-white px-4 py-3 sm:py-2 rounded-md shadow-md hover:bg-blue-600 transition duration-300 min-h-[44px] sm:min-h-0 flex items-center justify-center"
            >
              <span className="flex items-center">
                Changer le statut <IoMdOptions className="ml-2 flex-shrink-0" />
              </span>
            </button>
          </div>

          {showModal && (
            <div className="fixed inset-0 z-[9999] overflow-y-auto">
              <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity">
                  <div
                    className="absolute inset-0 bg-gray-500 opacity-75"
                    onClick={closeModal}
                  ></div>
                </div>

                <span
                  className="hidden sm:inline-block sm:align-middle sm:h-screen"
                  aria-hidden="true"
                >
                  &#8203;
                </span>

                <div
                  className="relative z-[10000] inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="modal-headline"
                >
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full sm:w-100">
                      <h3
                        className="text-xl sm:text-2xl leading-6 font-bold text-gray-900"
                        id="modal-headline"
                      >
                        Update Status
                      </h3>
                      <div className="mt-4 relative">
                        <select
                          id="status"
                          name="status"
                          value={status}
                          onChange={handleStatusChange}
                          className="block appearance-none w-full bg-white border border-gray-300 text-gray-700 py-3 px-4 pr-8 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-lg"
                        >
                          <option value="pending">En cours</option>
                          <option value="rejected">Rejetée</option>
                          <option value="approved">Approuvée</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <svg
                            className="h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 12a1 1 0 00.707-.293l3-3a1 1 0 10-1.414-1.414L10 9.586 7.707 7.293a1 1 0 00-1.414 1.414l3 3a1 1 0 00.707.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>

                      <div className="mt-4">
                        <label
                          htmlFor="Commentaire"
                          className="block text-xl font-medium text-gray-700"
                        >
                          Commentaire
                        </label>
                        <textarea
                          id="Commentaire"
                          onChange={handleCommentChange}
                          value={comment}
                          name="Commentaire"
                          className="mt-1 block w-full bg-slate-50 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 border-gray-300 rounded-md py-3 px-4 sm:text-lg"
                          rows="3"
                          placeholder="Entrez votre commentaire ici..."
                        ></textarea>
                      </div>

                      <div className="mt-4">
                        <button
                          onClick={handleSubmit}
                          className="w-full flex justify-center items-center px-4 py-2 rounded-md bg-indigo-600 text-white font-medium text-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Sauvegarder
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="firstName"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Prénom
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={emptyValue(recordData.firstName)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="lastName"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Nom de famille
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={emptyValue(recordData.lastName)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="dateNaissance"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Age
              </label>
              <input
                type="text"
                id="dateNaissance"
                name="dateNaissance"
                value={recordData.dateNaissance ? new Date(recordData.dateNaissance).toLocaleDateString("fr-FR") : "—"}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="lieuNaissance"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Lieu de Naissance
              </label>
              <input
                type="text"
                id="lieuNaissance"
                name="lieuNaissance"
                value={emptyValue(recordData.lieuNaissance)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="sexe"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Sexe
              </label>
              <input
                type="text"
                id="sexe"
                name="sexe"
                value={emptyValue(recordData.sexe)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="adresse"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Adresse
              </label>
              <input
                type="text"
                id="adresse"
                name="adresse"
                value={emptyValue(recordData.adresse)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="ville"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Ville
              </label>
              <input
                type="text"
                id="ville"
                name="ville"
                value={emptyValue(recordData.ville)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="pays"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Pays
              </label>
              <input
                type="text"
                id="pays"
                name="pays"
                value={emptyValue(recordData.pays)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="codePostal"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Code Postal
              </label>
              <input
                type="text"
                id="codePostal"
                name="codePostal"
                value={emptyValue(recordData.codePostal)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="telephone"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Téléphone
              </label>
              <input
                type="text"
                id="telephone"
                name="telephone"
                value={emptyValue(recordData.telephone)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="numeroSecurite"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Numéro de Sécurité Sociale
              </label>
              <input
                type="text"
                id="numeroSecurite"
                name="numeroSecurite"
                value={emptyValue(recordData.numeroSecurite)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="CNAPS"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                CNAPS
              </label>
              <input
                type="text"
                id="CNAPS"
                name="CNAPS"
                value={emptyValue(recordData.CNAPS)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="dateValiditeAgrementCNAPS"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Date de validité de l'agrément CNAPS
              </label>
              <input
                type="text"
                id="dateValiditeAgrementCNAPS"
                name="dateValiditeAgrementCNAPS"
                value={recordData.dateValiditeAgrementCNAPS ? new Date(recordData.dateValiditeAgrementCNAPS).toLocaleDateString("fr-FR") : "—"}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="poste"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Poste
              </label>
              <input
                type="text"
                id="poste"
                name="poste"
                value={emptyValue(recordData.poste)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="englishLevel"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Niveau d'anglais
              </label>
              <input
                type="text"
                id="englishLevel"
                name="englishLevel"
                value={recordData.englishLevel != null ? `${recordData.englishLevel} / 5` : "—"}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="permisVoiture"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Permis de conduire
              </label>
              <input
                type="text"
                id="permisVoiture"
                name="permisVoiture"
                value={emptyValue(recordData.permisVoiture)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="vehicule"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Véhicule
              </label>
              <input
                type="text"
                id="vehicule"
                name="vehicule"
                value={emptyValue(recordData.vehicule)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Email
              </label>
              <input
                type="text"
                id="email"
                name="email"
                value={emptyValue(recordData.email)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="nationality"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Nationalité
              </label>
              <input
                type="text"
                id="nationality"
                name="nationality"
                value={emptyValue(recordData.nationality)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>
            <div>
              <label
                htmlFor="numeroCIN"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Numéro de CIN
              </label>
              <input
                type="text"
                id="numeroCIN"
                name="numeroCIN"
                value={emptyValue(recordData.numeroCIN)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />
            </div>

            <div>
              <label
                htmlFor="recommande"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Recommandé par quelqu'un
              </label>
              <input
                type="text"
                id="recommande"
                name="recommande"
                value={emptyValue(recordData.recommande)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
              />

              {recordData.recommande === "Oui" && (
                <div className="mt-4">
                  <div>
                    <label
                      htmlFor="recommenderName"
                      className="block text-lg mb-2 font-medium text-gray-700"
                    >
                      Nom de la personne qui vous recommande
                    </label>
                    <input
                      type="text"
                      id="recommenderName"
                      name="recommenderName"
                      value={emptyValue(recordData.recommenderName)}
                      disabled
                      className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
                    />
                  </div>

                  <div className="mt-4">
                    <label
                      htmlFor="recommenderPhone"
                      className="block text-lg mb-2 font-medium text-gray-700"
                    >
                      Numéro de téléphone de la personne qui vous recommande
                    </label>
                    <input
                      type="tel"
                      id="recommenderPhone"
                      name="recommenderPhone"
                      value={emptyValue(recordData.recommenderPhone)}
                      disabled
                      className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
                    />
                  </div>
                  <div className="mt-4">
                    <label
                      htmlFor="healthCheck"
                      className="block text-lg mb-2 font-medium text-gray-700"
                    >
                      Avez-vous une fiche d’aptitude médicale ?
                    </label>
                    <input
                      type="text"
                      id="healthCheck"
                      name="healthCheck"
                      value={
                        recordData.healthCheck === "Yes" ? "Oui" : recordData.healthCheck === "No" ? "Non" : "—"
                      }
                      disabled
                      className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
                    />
                  </div>

                  <div className="mt-4">
                    <label
                      htmlFor="wantHealthCheck"
                      className="block text-lg mb-2 font-medium text-gray-700"
                    >
                      Souhaitez-vous effectuer une visite médicale ?
                    </label>
                    <input
                      type="text"
                      id="wantHealthCheck"
                      name="wantHealthCheck"
                      value={
                        recordData.wantHealthCheck === "Yes" ? "Oui" : recordData.wantHealthCheck === "No" ? "Non" : "—"
                      }
                      disabled
                      className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="comment"
                className="block text-lg mb-2 font-medium text-gray-700"
              >
                Commentaire
              </label>
              <textarea
                id="comment"
                name="comment"
                value={emptyValue(recordData.comment)}
                disabled
                className="mt-1 border border-gray-200 px-3 py-2 rounded-lg w-full bg-gray-50 text-gray-800"
                rows="3"
              />
            </div>
          </div>
          <div>
            {fileloading ? (
              <div className="mt-16 mb-4 flex items-center gap-3 text-blue-700">
                <div className="h-5 w-5 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
                <h3 className="text-xl font-semibold w-full">
                  Chargement des fichiers…
                </h3>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mt-16 mb-4 w-full">
                  <h3 className="text-xl font-semibold text-gray-700">
                    Fichiers associés:
                  </h3>
                  <button
                    onClick={handleDownloadAll}
                    disabled={downloading.active}
                    className="flex items-center bg-teal-500 text-white px-4 py-2 rounded-md shadow-md hover:bg-teal-600 transition duration-300"
                  >
                    <AiOutlineDownload className="mr-2" /> Télécharger tous les
                    fichiers
                  </button>
                </div>
              </>
            )}
            <ul className="space-y-2 ">
              {!fileloading &&
                Object.entries(fileUrls).map(([key, url]) => (
                  <li className="" key={key}>
                    <div className="flex justify-between ">
                      <button
                        onClick={() => handleFileClick(key, url)}
                        className={`block ${selectedFileKey === key ? "bg-blue-200" : "bg-gray-200"
                          } min-w-4xl hover:bg-gray-300 px-4 py-4 rounded-lg text-lg text-gray-800 hover:text-gray-900 transition duration-300`}
                      >
                        {getFileLabel(key)}
                      </button>
                    </div>
                    <div className="mt-2 mb-8">
                      <button
                        disabled={downloading.active}
                        onClick={() => handleDownload(key, url)}
                        className="text-left text-lg hover:underline text-blue-600 rounded"
                      >
                        Télécharger
                      </button>
                    </div>
                    {selectedFileKey === key && (
                      <div className="mt-4">
                        <iframe
                          src={selectedFileUrl}
                          title="File Preview"
                          width="100%"
                          height="700px"
                          seamless
                        ></iframe>
                      </div>
                    )}
                  </li>
                ))}
            </ul>
          </div>
        </div>
        <Toaster />
      </div>
    </>
  );
};

const getFileLabel = (key) => {
  switch (key) {
    case "idCard":
      return "Carte d'identité Verso";
    case "idCardRecto":
      return "Carte d'identité Recto";
    case "carteVitale":
      return "Carte Vitale";
    case "carteProfessionnelle":
      return "Carte Professionnelle";
    case "diplomes":
      return "Diplômes";
    case "justificatif":
      return "Justificatif";
    case "rib":
      return "RIB";
    case "photo":
      return "Photo";
    case "cv":
      return "CV";
    case "attestationMedical":
      return "Attestation de suivi médical";
    case "healthCheckFile":
      return "Bilan de santé";
    default:
      return "";
  }
};

export default ViewRecordDetails;